import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ExternalLink, Loader2, PackageCheck, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDateTime } from './orderConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIRECTIONS = { to_cabinet: 'Vers le cabinet', to_fabricant: 'Vers le laboratoire' };

export const ShipmentCard = ({ order, onChanged }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [shipments, setShipments] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [form, setForm] = useState({ carrier: '', tracking_number: '', direction: 'to_cabinet' });
  const [busy, setBusy] = useState(false);

  const fetchShipments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/orders/${order.id}/shipments`);
      setShipments(data);
    } catch (e) { setShipments([]); }
  }, [order.id]);

  useEffect(() => {
    fetchShipments();
    if (isAdmin) axios.get(`${API}/shipments/carriers`).then((r) => setCarriers(r.data)).catch(() => {});
  }, [fetchShipments, isAdmin]);

  const createShipment = async (e) => {
    e.preventDefault();
    if (!form.carrier) { toast.error('Choisissez un transporteur'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/orders/${order.id}/shipments`, form);
      toast.success(data.order_transitioned ? 'Expédition créée — dossier passé au statut « Expédié »' : 'Expédition créée');
      setForm({ carrier: '', tracking_number: '', direction: 'to_cabinet' });
      fetchShipments();
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création de l'expédition");
    } finally { setBusy(false); }
  };

  const markDelivered = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/shipments/${id}/delivered`);
      toast.success('Expédition marquée livrée');
      fetchShipments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  if (!isAdmin && (!shipments || shipments.length === 0)) return null;

  return (
    <Card className="border-border" data-testid="shipment-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><Truck className="h-4 w-4" />Expéditions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {shipments === null ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : shipments.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune expédition enregistrée.</p>
        ) : (
          <ul className="divide-y divide-border">
            {shipments.map((s) => (
              <li key={s.id} className="py-2 flex items-center justify-between gap-2 flex-wrap" data-testid={`shipment-row-${s.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {s.carrier_label}
                    {s.tracking_number && <span className="font-mono text-muted-foreground"> · {s.tracking_number}</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isAdmin && `${DIRECTIONS[s.direction] || s.direction} · `}Expédié le {fmtDateTime(s.shipped_at)}
                    {s.delivered_at && ` · Livré le ${fmtDateTime(s.delivered_at)}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={s.status === 'delivered' ? 'text-emerald-700 border-emerald-300' : 'text-sky-700 border-sky-300'}>
                    {s.status === 'delivered' ? 'Livré' : 'Expédié'}
                  </Badge>
                  {s.tracking_url && (
                    <a href={s.tracking_url} target="_blank" rel="noreferrer" data-testid={`shipment-track-${s.id}`}>
                      <Button size="sm" variant="outline" className="gap-1"><ExternalLink className="h-3.5 w-3.5" />Suivre</Button>
                    </a>
                  )}
                  {isAdmin && s.status !== 'delivered' && (
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => markDelivered(s.id)} data-testid={`shipment-delivered-${s.id}`}>
                      <PackageCheck className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {isAdmin && (
          <form onSubmit={createShipment} className="grid sm:grid-cols-4 gap-2 items-end border-t border-border pt-3">
            <div className="space-y-1">
              <Label className="text-xs">Transporteur</Label>
              <Select value={form.carrier} onValueChange={(v) => setForm({ ...form, carrier: v })}>
                <SelectTrigger data-testid="shipment-carrier-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {carriers.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">N° de suivi</Label>
              <Input value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
                placeholder="1Z999AA1..." data-testid="shipment-tracking-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Direction</Label>
              <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                <SelectTrigger data-testid="shipment-direction-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="to_cabinet">Vers le cabinet</SelectItem>
                  <SelectItem value="to_fabricant">Vers le laboratoire</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={busy} className="gap-2" data-testid="shipment-create-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}Expédier
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
};
